import React, { useState, useMemo, useCallback, useEffect } from 'react';
import DatePicker from '../../../components/DatePicker/DatePicker';
import AppointmentControls from './AppointmentControls/AppointmentControls';
import Button from '../../../components/Button/Button';
import { DndContext } from '@dnd-kit/core';
import styles from './Appointments.module.css';
import ScheduleList from './Schedul/ScheduleList';
import AddEditAppointment from './AddEditAppointment/AddEditAppointment';
import AppointmentPaymentDetail from '../../../components/AppointmentPaymentDetail/AppointmentPaymentDetail';
import { useToast } from '../../../contexts/ToastContext';
// AB#2052 — appointment datetimes are the salon's wall-clock stamped `…Z`;
// format them in UTC so times aren't shifted to the viewer's timezone.
import {
    formatAppointmentTime as formatAppointmentTimeUtc,
    formatAppointmentDate as formatAppointmentDateUtc,
} from '../../../lib/appointmentTime';
import { useAppointmentsOwner } from '../../../hooks/useAppointmentsOwner';
import { useCategories } from '../../../hooks/useCategories';
import { useBranches } from '../../../hooks/useBranches';
// AB#1751 — Calendar v1 C5 quick-book launcher (Epic AB#1746).
import QuickBookLauncher from '../../../components/QuickBookModal/QuickBookLauncher';

export interface ScheduleAppointment {
    id: string;
    service: string;
    customer: string;
    contact: string;
    duration: string;
    price: string;
    status: string;
    image: string;
    time: string;
    date: string;
}

export interface ApiAppointment {
    id: number;
    serviceName: string;
    customerName: string;
    clientName?: string;
    phoneNumber: string;
    clientPhone?: string;
    email: string;
    clientEmail?: string;
    appointmentDate: string;
    duration: string;
    servicePrice: number;
    paymentStatus: number;
    status: number;
    statusString: string;
    paymentStatusString: string;
    isDraft: boolean;
    staffId?: number;
    staffName?: string;
    isRegisteredCustomer: boolean;
}

const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const getMonthYearFromDateString = (dateStr: string) => {
    // dd_mm_yyyy split — day component is intentionally unused (label is just
    // "Month, Year"). Prefixed-elision satisfies strict CI=true ESLint.
    const [, mm, yyyy] = dateStr.split('/').map(Number);
    const monthIndex = Math.max(0, Math.min(11, (mm ?? 1) - 1));
    const year = yyyy ?? new Date().getFullYear();
    return `${monthNames[monthIndex]}, ${year}`;
};

const Appointments: React.FC = () => {
    const [scheduleData, setScheduleData] = useState<ScheduleAppointment[]>([]);
    const [isAddEditOpen, setIsAddEditOpen] = useState(false);
    const [editingAppointment, setEditingAppointment] = useState<string | null>(null);
    const [paymentDetailAppointmentId, setPaymentDetailAppointmentId] = useState<number | null>(null);
    const [selectedDate, setSelectedDate] = useState<string>(() => {
        const today = new Date();
        return today.toLocaleDateString('en-GB');
    });
    const [selectedDate0, setSelectedDate0] = useState<string>(() => {
        const today = new Date();
        return today.toLocaleDateString('en-GB');
    });
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    // Filter states
    const [paymentStatusFilter, setPaymentStatusFilter] = useState<string | null>(null);
    const [categoryFilter, setCategoryFilter] = useState<number | null>(null);

    const { getAppointments, deleteAppointment, rescheduleAppointment } = useAppointmentsOwner();
    const { getBusinessCategories } = useCategories();
    const { getBranches } = useBranches();
    const { showToast } = useToast();

    const [categories, setCategories] = useState<Array<{ id: number, name: string }>>([]);
    const [branches, setBranches] = useState<Array<{ id: number, name: string }>>([]);
    const [branchFilter, setBranchFilter] = useState<number | null>(null);

    // Load categories and branches on mount
    useEffect(() => {
        const loadData = async () => {
            try {
                const [categoriesResponse, branchesResponse] = await Promise.all([
                    getBusinessCategories(),
                    getBranches()
                ]);

                if (categoriesResponse?.categories) {
                    setCategories(categoriesResponse.categories.map(c => ({ id: c.id, name: c.name })));
                }

                if (branchesResponse?.branches) {
                    setBranches(branchesResponse.branches.map(b => ({ id: b.id, name: b.name })));
                }
            } catch (error) {
                console.error('Failed to load filter data:', error);
            }
        };
        loadData();
    }, [getBusinessCategories, getBranches]);

    // AB#2052 — wall-clock (UTC) formatters. The datetime is stamped `…Z` but
    // represents the salon's wall-clock time, so it must be extracted in UTC
    // (a 10:00Z booking shows 10:00, never 14:00 at UAE +4). Shared impl lives
    // in src/lib/appointmentTime.ts. `round15` preserves the legacy schedule-
    // grid 15-minute snap.
    const formatAppointmentDate = useCallback(
        (dateTimeString: string) => formatAppointmentDateUtc(dateTimeString),
        [],
    );

    const formatAppointmentTime = useCallback(
        (dateTimeString: string) => formatAppointmentTimeUtc(dateTimeString, { round15: true }),
        [],
    );

    const handleDateChange = useCallback((date: string) => {
        setSelectedDate(date);
    }, []);

    const handleTimeSlotSelect = useCallback((time: string) => {
        setSelectedTimeSlot(time);
    }, []);

    const convertTimeToAPIFormat = useCallback((timeSlot: string | null) => {
        if (!timeSlot) return '';
        const [time, period] = timeSlot.split(' ');
        const [hours, minutes] = time.split(':');
        let hour = parseInt(hours);

        if (period === 'PM' && hour !== 12) {
            hour += 12;
        } else if (period === 'AM' && hour === 12) {
            hour = 0;
        }

        return `${hour.toString().padStart(2, '0')}:${minutes}`;
    }, []);

    const refreshAppointments = useCallback(async () => {
        setIsLoading(true);
        setHasError(false);
        try {
            const [dd, mm, yyyy] = selectedDate.split('/').map(Number);
            const d = new Date(yyyy, mm - 1, dd);
            d.setDate(d.getDate() - d.getDay());
            const weekStartStr = `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
            const fromDateParts = weekStartStr.split('/');
            const startDate = `${fromDateParts[2]}-${fromDateParts[1]}-${fromDateParts[0]}`;

            const toDateObj = new Date(parseInt(fromDateParts[2]), parseInt(fromDateParts[1]) - 1, parseInt(fromDateParts[0]));
            toDateObj.setDate(toDateObj.getDate() + 6);
            const endDate = toDateObj.toISOString().split('T')[0];

            const response = await getAppointments({
                startDate,
                endDate,
                branchId: branchFilter ?? undefined,
                paymentStatus: paymentStatusFilter ?? undefined,
                categoryId: categoryFilter ?? undefined,
                page: 1,
                pageSize: 50
            });

            const transformedData: ScheduleAppointment[] = (response.appointments || []).map(appointment => {
                const [durationHours, durationMinutes] = appointment.duration.split(':').map(Number);
                const formattedDuration = `${durationHours}h ${durationMinutes}min`;

                return {
                    id: appointment.id.toString(),
                    service: appointment.serviceName,
                    customer: appointment.customerName,
                    contact: appointment.phoneNumber,
                    duration: formattedDuration,
                    price: `${(appointment as { servicePrice?: number; amount?: number }).servicePrice ?? appointment.amount ?? 0} AED`,
                    status: appointment.paymentStatusString,
                    image: './assets/icons/avatar.png',
                    time: formatAppointmentTime(appointment.appointmentDate),
                    date: formatAppointmentDate(appointment.appointmentDate)
                };
            });

            setScheduleData(transformedData);
        } catch (error: any) {
            // #1197: Treat 404/204 as empty state, not error state
            const status = error?.response?.status;
            if (status === 404 || status === 204) {
                setScheduleData([]);
            } else {
                setHasError(true);
                showToast('Error loading appointments. Please try again.', 'error');
            }
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }, [selectedDate, branchFilter, paymentStatusFilter, categoryFilter, getAppointments, showToast, formatAppointmentTime, formatAppointmentDate]);

    const handleDragEnd = useCallback(async (data: ScheduleAppointment[], movedAppointmentData?: { appointmentId: string, newTime: string, newDate: string }) => {
        if (movedAppointmentData) {
            try {
                // Convert the new date and time to local YYYY-MM-DDTHH:mm:ss (no UTC)
                const [day, month, year] = movedAppointmentData.newDate.split('/').map(Number);
                const [time, period] = movedAppointmentData.newTime.split(' ');
                const [hours, minutes] = time.split(':').map(Number);

                let finalHours = hours;
                if (period === 'PM' && hours !== 12) {
                    finalHours += 12;
                } else if (period === 'AM' && hours === 12) {
                    finalHours = 0;
                }

                const newDateTime = new Date(year, month - 1, day, finalHours, minutes);
                const offsetMin = -newDateTime.getTimezoneOffset();
                const offsetSign = offsetMin >= 0 ? '+' : '-';
                const offsetHours = Math.floor(Math.abs(offsetMin) / 60).toString().padStart(2, '0');
                const offsetMins = (Math.abs(offsetMin) % 60).toString().padStart(2, '0');
                const localWithOffset = `${newDateTime.getFullYear()}-${(newDateTime.getMonth() + 1).toString().padStart(2, '0')}-${newDateTime.getDate().toString().padStart(2, '0')}T${newDateTime.getHours().toString().padStart(2, '0')}:${newDateTime.getMinutes().toString().padStart(2, '0')}:00${offsetSign}${offsetHours}:${offsetMins}`;

                if (process.env.NODE_ENV === 'development') console.log('Rescheduling appointment:', {
                    id: movedAppointmentData.appointmentId,
                    from: scheduleData.find(a => a.id === movedAppointmentData.appointmentId),
                    to: { date: movedAppointmentData.newDate, time: movedAppointmentData.newTime },
                    localWithOffset
                });

                await rescheduleAppointment(parseInt(movedAppointmentData.appointmentId), localWithOffset);
                showToast('Appointment rescheduled successfully', 'success');
                setScheduleData(data);
            } catch (error) {
                showToast('Failed to reschedule appointment', 'error');
                console.error('Reschedule error:', error);
                // Revert the change on error
                refreshAppointments();
            }
        } else {
            setScheduleData(data);
        }
    }, [scheduleData, rescheduleAppointment, showToast, refreshAppointments]);

    const getDateRange = useCallback((startDate: string) => {
        const [dd, mm, yyyy] = startDate.split('/').map(Number);
        const start = new Date(yyyy, mm - 1, dd);
        return Array.from({ length: 7 }, (_, i) => {
            const currentDate = new Date(start);
            currentDate.setDate(start.getDate() + i);
            return currentDate.toLocaleDateString('en-GB');
        });
    }, []);

    const weekStart = useMemo(() => {
        const [dd, mm, yyyy] = selectedDate.split('/').map(Number);
        const d = new Date(yyyy, mm - 1, dd);
        d.setDate(d.getDate() - d.getDay());
        return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
    }, [selectedDate]);

    const dateRange = useMemo(() => getDateRange(weekStart), [weekStart, getDateRange]);

    const filteredScheduleData = useMemo(() =>
        scheduleData.filter(item => dateRange.includes(item.date)),
        [scheduleData, dateRange]);

    const currentMonthYear = useMemo(() => getMonthYearFromDateString(selectedDate), [selectedDate]);

    useEffect(() => {
        refreshAppointments();
    }, [refreshAppointments]);

    const handleAddAppointmentSuccess = useCallback(() => {
        showToast('Appointment created successfully', 'success');
        refreshAppointments();
        setIsAddEditOpen(false);
        setEditingAppointment(null);
    }, [showToast, refreshAppointments]);

    const handleEditAppointment = useCallback((appointmentId: string) => {
        setEditingAppointment(appointmentId);
        setIsAddEditOpen(true);
    }, []);

    const handleViewPayment = useCallback((appointmentId: string) => {
        setPaymentDetailAppointmentId(parseInt(appointmentId));
    }, []);

    const handleDeleteAppointment = useCallback(async (appointmentId: string) => {
        try {
            await deleteAppointment(parseInt(appointmentId));
            showToast('Appointment deleted successfully', 'success');
            refreshAppointments();
        } catch {
            showToast('Failed to delete appointment', 'error');
        }
    }, [deleteAppointment, showToast, refreshAppointments]);

    const handleDateClick = useCallback((date: string) => {
        setSelectedDate0(date);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsAddEditOpen(false);
        setEditingAppointment(null);
    }, []);

    const handleDropToPast = useCallback(() => {
        showToast('Cannot reschedule to a past time', 'error');
    }, [showToast]);

    return (
        <div className={styles.appointmentsMainContainer}>
            <div className={styles.controlsHeaderContainer}>
                <h1 className='xH1'>{currentMonthYear}</h1>
                <div className={styles.controlsContainer}>
                    <AppointmentControls
                        branches={branches}
                        categories={categories}
                        onBranchChange={setBranchFilter}
                        onPaymentStatusChange={setPaymentStatusFilter}
                        onCategoryChange={setCategoryFilter}
                    />
                    <Button
                        label="New appointment +"
                        variant="primary"
                        size="medium"
                        onClick={() => setIsAddEditOpen(true)}
                    />
                    {/* AB#1751 — service-first quick-book modal launcher.
                        Resolves the active branch from the existing
                        branchFilter (when set) or falls back to the first
                        active branch in the loaded list. */}
                    <QuickBookLauncher
                        branchId={branchFilter ?? branches.find(b => b)?.id ?? null}
                        branchName={branches.find(b => b.id === branchFilter)?.name}
                        onBooked={refreshAppointments}
                    />
                </div>
            </div>
            <DatePicker
                onDateChange={handleDateChange}
                selectedDate={selectedDate}
                onDateClick={handleDateClick}
            />

            {isLoading ? (
                <div className={styles.loadingContainer}>
                    <div className={styles.spinner}>
                        <svg viewBox="0 0 50 50">
                            <circle cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
                        </svg>
                        <span>Loading appointments...</span>
                    </div>
                </div>
            ) : hasError ? (
                <div className={styles.emptyState}>
                    <p>Error loading appointments. Please try again.</p>
                    <Button
                        label="Retry"
                        onClick={refreshAppointments}
                        size="small"
                    />
                </div>
            ) : filteredScheduleData.length === 0 ? (
                <div className={styles.emptyState}>
                    <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <circle style={{ stroke: 'var(--primary-subtle)' }} cx="60" cy="60" r="55" strokeWidth="2" />
                        <rect style={{ stroke: 'var(--primary)' }} x="35" y="30" width="50" height="55" rx="6" strokeWidth="2" fill="none" />
                        <line style={{ stroke: 'var(--primary)' }} x1="35" y1="45" x2="85" y2="45" strokeWidth="2" />
                        <circle style={{ fill: 'var(--primary)' }} cx="47" cy="37" r="2" />
                        <circle style={{ fill: 'var(--primary)' }} cx="73" cy="37" r="2" />
                        <line style={{ stroke: 'var(--primary)' }} x1="47" y1="28" x2="47" y2="35" strokeWidth="2" strokeLinecap="round" />
                        <line style={{ stroke: 'var(--primary)' }} x1="73" y1="28" x2="73" y2="35" strokeWidth="2" strokeLinecap="round" />
                        <line style={{ stroke: 'var(--primary-subtle)' }} x1="45" y1="55" x2="55" y2="55" strokeWidth="2" strokeLinecap="round" />
                        <line style={{ stroke: 'var(--primary-subtle)' }} x1="45" y1="63" x2="65" y2="63" strokeWidth="2" strokeLinecap="round" />
                        <line style={{ stroke: 'var(--primary-subtle)' }} x1="45" y1="71" x2="60" y2="71" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <h2 className={styles.emptyStateTitle}>No appointments this week</h2>
                    <p className={styles.emptyStateSubtitle}>Create your first appointment to get started</p>
                    <Button
                        label="New appointment +"
                        variant="primary"
                        size="medium"
                        onClick={() => setIsAddEditOpen(true)}
                    />
                </div>
            ) : (
                <DndContext>
                    <ScheduleList
                        scheduleData={filteredScheduleData}
                        dateRange={dateRange}
                        onDragEnd={handleDragEnd}
                        onTimeSlotSelect={handleTimeSlotSelect}
                        onEdit={handleEditAppointment}
                        onDelete={handleDeleteAppointment}
                        onViewPayment={handleViewPayment}
                        onDropToPast={handleDropToPast}
                    />
                </DndContext>
            )}

            <AddEditAppointment
                isOpen={isAddEditOpen}
                onClose={handleCloseModal}
                onSuccess={handleAddAppointmentSuccess}
                selectedTime={convertTimeToAPIFormat(selectedTimeSlot)}
                selectedDate={selectedDate0}
                editingAppointmentId={editingAppointment}
                businessId={0}
            />

            <AppointmentPaymentDetail
                isOpen={paymentDetailAppointmentId !== null}
                appointmentId={paymentDetailAppointmentId ?? 0}
                onClose={() => setPaymentDetailAppointmentId(null)}
            />
        </div>
    );
};

export default Appointments;